/* eslint-disable react/jsx-props-no-spreading */
import React from 'react';
import proptypes from 'prop-types';
import { ErrorBoundary } from '@thd-olt-component-react/error-boundary';
import { useStore } from '@thd-nucleus/experience-context';
import { useLocation, useParams } from '@thd-olt-component-react/router';
import { QueryProvider } from '@thd-nucleus/data-sources';
import Product from './Product';
import CollapsibleSections from '../collapsible-sections';
import { getLocalizerZipCode } from '../../utils/fulfillment-utils';
import DraftBanner from '../../components/DraftBanner';
import { DesignStoreProvider } from '../../store/design/context';
import { useProductInfoStore } from '../../store';

import '../../styles/product/product.scss';

const { oneOfType, string, number } = proptypes;

export const CollapsibleSectionsDataModel = {
  prefetchPip: CollapsibleSections.dataModel.product,
  prefetchPipClient: CollapsibleSections.dataModel.clientOnlyProduct
};

export const ProductContainer = ({ pipTimeout, channel, ...props }) => {
  const { itemId } = useParams();
  const { search } = useLocation();
  const searchParams = new URLSearchParams(search);
  const draftProductId = searchParams.get('draftProductId');
  const pipOmsId = searchParams.get('pipOmsId');
  const store = useStore();
  const { storeId } = store;
  const zipCode = getLocalizerZipCode();
  const disableDropzoneFilter = useProductInfoStore((state) => state.productInfo?.disableDropzoneFilter);

  const pipQueryOptions = ({ options, queryName }) => {
    if (queryName === 'product') {
      let to = 3000;
      if (pipTimeout) {
        if (typeof pipTimeout === 'string') {
          to = parseInt(pipTimeout, 10);
          // eslint-disable-next-line no-restricted-globals
          if (isNaN(to)) {
            to = 3000;
          }
        } else if (typeof pipTimeout === 'number') {
          to = pipTimeout;
        }
      }
      return {
        ...options,
        context: {
          ...(options.context || {}),
          timeout: to
        }
      };
    }
    if (queryName === 'productClientOnlyProduct') {
      return {
        ...options,
        context: {
          ...(options.context || {}),
          withAuth: true
        }
      };
    }
    return options;
  };

  const pipDefaultVariables = {
    filters: {
      isVerifiedPurchase: false,
      prosCons: null,
      starRatings: null
    },
    searchTerm: null,
    sortBy: 'photoreview',
    startIndex: 1,
    maxItems: 6,
    storeId,
    zipCode
  };

  const merge = {
    productClientOnlyProduct: {
      queries: ['product', 'clientOnlyProduct'],
      fn: ({ query, mounted }) => {

        if (!mounted) return query;

        if (query === 'product' || query === 'clientOnlyProduct') {
          return 'product';
        }
        return query;
      }
    }
  };

  const dropzoneStyleOverrides = disableDropzoneFilter
    ? {
      '--dropzone-filter': 'none',
      '--dropzone-filter-hover': 'none'
    }
    : undefined;

  return (
    <div className="grid isBound" data-sub-experience="product" style={dropzoneStyleOverrides}>
      <DraftBanner draftProductId={draftProductId} />
      <DesignStoreProvider uniqueId={itemId}>
        <Product
          {...props}
          channel={channel}
          store={store}
          draftProductId={draftProductId}
          pipOmsId={pipOmsId}
          itemId={itemId}
        />
      </DesignStoreProvider>
      <ErrorBoundary name="eb-product-page">
        <QueryProvider
          defaultVariables={pipDefaultVariables}
          queryOptions={pipQueryOptions}
          merge={merge}
          cacheKey={`product-page-${channel}`}
        >
          <CollapsibleSections store={store} />
        </QueryProvider>
      </ErrorBoundary>
    </div>
  );
};

ProductContainer.propTypes = {
  pipTimeout: oneOfType([string, number]),
  channel: string
};

ProductContainer.defaultProps = {
  pipTimeout: '3000',
  channel: 'desktop'
};
